import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const GSTComplianceSection = ({
  gstData = {},
  invoices = [],
  dateRange = {},
  loading = false,
}) => {
  const currentPeriod = {
    period:
      dateRange.start && dateRange.end
        ? `${new Date(dateRange.start).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
          })} - ${new Date(dateRange.end).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
          })}`
        : "Current Period",
    totalSales: invoices.reduce((sum, inv) => sum + (inv.grandTotal || 0), 0),
    cgst: gstData.cgst || 0,
    sgst: gstData.sgst || 0,
    igst: gstData.igst || 0,
    totalTax: gstData.total || 0,
    filingStatus: "pending",
    dueDate: "20/11/2025",
  };

  const totalTaxable = invoices.reduce(
    (sum, inv) => sum + (inv.subtotal || 0),
    0
  );
  const cgstTaxable = invoices
    .filter((inv) => (inv.totalCgst || 0) > 0)
    .reduce((sum, inv) => sum + (inv.subtotal || 0), 0);
  const sgstTaxable = cgstTaxable; // Same as CGST taxable
  const igstTaxable = invoices
    .filter((inv) => (inv.totalIgst || 0) > 0)
    .reduce((sum, inv) => sum + (inv.subtotal || 0), 0);

  const gstBreakdown = [
    {
      type: "CGST",
      rate: "9%",
      taxableAmount: cgstTaxable,
      taxAmount: gstData.cgst || 0,
      color: "bg-blue-500",
    },
    {
      type: "SGST",
      rate: "9%",
      taxableAmount: sgstTaxable,
      taxAmount: gstData.sgst || 0,
      color: "bg-green-500",
    },
    {
      type: "IGST",
      rate: "18%",
      taxableAmount: igstTaxable,
      taxAmount: gstData.igst || 0,
      color: "bg-purple-500",
    },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    })?.format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "filed":
        return "text-success bg-success/10";
      case "pending":
        return "text-warning bg-warning/10";
      case "overdue":
        return "text-destructive bg-destructive/10";
      default:
        return "text-text-secondary bg-muted";
    }
  };

  if (loading) {
    return (
      <div className="bg-card bg-white rounded-lg border border-border p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground">
            GST Compliance
          </h2>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card bg-white rounded-lg border border-border p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <h2 className="text-lg sm:text-xl font-semibold text-foreground">
          GST Compliance
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            className="w-full sm:w-auto"
          >
            Download GSTR-1
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            className="w-full sm:w-auto"
          >
            Download GSTR-3B
          </Button>
        </div>
      </div>
      {/* Current Period Summary */}
      <div className="bg-muted rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
          <h3 className="text-base sm:text-lg font-medium text-foreground">
            {currentPeriod?.period}
          </h3>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium self-start ${getStatusColor(
              currentPeriod?.filingStatus
            )}`}
          >
            <div className="flex items-center space-x-1">
              <Icon
                name={
                  currentPeriod?.filingStatus === "filed"
                    ? "CheckCircle"
                    : "Clock"
                }
                size={16}
              />
              <span className="capitalize">{currentPeriod?.filingStatus}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center">
            <p className="text-sm text-text-secondary mb-1">Total Sales</p>
            <p className="text-lg font-semibold text-foreground">
              {formatCurrency(currentPeriod?.totalSales)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-text-secondary mb-1">Total Tax</p>
            <p className="text-lg font-semibold text-foreground">
              {formatCurrency(currentPeriod?.totalTax)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-text-secondary mb-1">Due Date</p>
            <p className="text-lg font-semibold text-foreground">
              {currentPeriod?.dueDate}
            </p>
          </div>
          <div className="text-center">
            <Button
              variant={
                currentPeriod?.filingStatus === "pending"
                  ? "default"
                  : "outline"
              }
              size="sm"
              className="w-full"
            >
              {currentPeriod?.filingStatus === "pending"
                ? "File Return"
                : "View Filed"}
            </Button>
          </div>
        </div>
      </div>
      {/* GST Breakdown */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">
          Tax Breakdown
        </h3>
        <div className="space-y-4">
          {gstBreakdown?.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-surface-secondary rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className={`w-4 h-4 rounded-full ${item?.color}`} />
                <div>
                  <h4 className="font-medium text-foreground">{item?.type}</h4>
                  <p className="text-sm text-text-secondary">
                    Rate: {item?.rate}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-foreground">
                  {formatCurrency(item?.taxAmount)}
                </p>
                <p className="text-sm text-text-secondary">
                  on {formatCurrency(item?.taxableAmount)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            className="text-xs sm:text-sm"
          >
            Generate GSTR-1
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            className="text-xs sm:text-sm"
          >
            Generate GSTR-3B
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Calculator"
            className="text-xs sm:text-sm"
          >
            Tax Calculator
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="History"
            className="text-xs sm:text-sm"
          >
            Filing History
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GSTComplianceSection;
